import { useState } from 'react';
import clsx from 'clsx';

import { Button, Card, Icon } from '@lib/ui';
import { Steps, Step, AnimatedTick } from '@lib/ui';
import type { WizardPersona } from '../types';
import { defaultPersona } from '../types';
import { IdentityStep, PersonalityStep, VoiceStep, TestReviewStep } from './steps';

const STEPS = [
  { title: 'Identity', description: 'Name, audience, purpose' },
  { title: 'Voice', description: 'Traits and style' },
  { title: 'Decisions', description: 'Framing and tone' },
  { title: 'Test & Review', description: 'Generate samples' },
];

export type StepByStepWizardProps = {
  onComplete?: (persona: WizardPersona) => void;
  onCancel?: () => void;
};

export function StepByStepWizard({ onComplete, onCancel }: StepByStepWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [persona, setPersona] = useState<Partial<WizardPersona>>(defaultPersona);
  const [isCompleted, setIsCompleted] = useState(false);

  const updatePersona = (updates: Partial<WizardPersona>) => {
    setPersona((prev) => ({ ...prev, ...updates }));
  };

  const goNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goPrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    setIsCompleted(true);
    // Simulate completion animation
    setTimeout(() => {
      onComplete?.(persona as WizardPersona);
    }, 1500);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: // Identity
        return !!persona.name?.trim();
      case 1: // Voice
        return (persona.voice?.traits?.length || 0) > 0;
      case 2: // Decisions
        return true; // Decisions always have defaults
      case 3: // Test & Review
        return true;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <IdentityStep persona={persona} onChange={updatePersona} />;
      case 1:
        return <VoiceStep persona={persona} onChange={updatePersona} />;
      case 2:
        return <PersonalityStep persona={persona} onChange={updatePersona} />;
      case 3:
        return <TestReviewStep persona={persona} onChange={updatePersona} />;
      default:
        return null;
    }
  };

  // Completion screen
  if (isCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900 p-6">
        <Card skin="shadow" className="p-12 text-center max-w-md">
          <AnimatedTick color="success" size="xl" className="mx-auto mb-6" />
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
            Persona Created!
          </h2>
          <p className="text-gray-500 dark:text-dark-400">
            "{persona.name}" is ready to start creating content.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      {/* Header */}
      <div className="bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-600 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Create Persona</h1>
            <p className="text-sm text-gray-500 dark:text-dark-400">Step-by-step guided wizard</p>
          </div>
          <Button variant="flat" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>

      {/* Progress steps */}
      <div className="bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-600 px-6 py-6">
        <div className="max-w-4xl mx-auto">
          <Steps currentStep={currentStep} color="primary">
            {STEPS.map((step, index) => (
              <Step
                key={step.title}
                index={index}
                title={step.title}
                description={step.description}
                onClick={() => index < currentStep && setCurrentStep(index)}
              />
            ))}
          </Steps>
        </div>
      </div>

      {/* Step content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Card skin="shadow" className="p-6 md:p-8">
          <div key={currentStep} className="animate-in fade-in slide-in-from-right-2 duration-300">
            {renderStepContent()}
          </div>
        </Card>

        {/* Navigation */}
        <div className="mt-6 flex items-center justify-between">
          <Button
            variant="outlined"
            onClick={goPrev}
            disabled={currentStep === 0}
            className={clsx(currentStep === 0 && 'opacity-0 pointer-events-none')}
          >
            <Icon name="arrow-left" size="md" />
            <span className="ml-1">Previous</span>
          </Button>

          <div className="text-sm text-gray-500 dark:text-dark-400">
            Step {currentStep + 1} of {STEPS.length}
          </div>

          {currentStep < STEPS.length - 1 ? (
            <Button onClick={goNext} disabled={!canProceed()}>
              <span className="mr-1">Next</span>
              <Icon name="arrow-right" size="md" />
            </Button>
          ) : (
            <Button onClick={handleComplete} color="success" disabled={!canProceed()}>
              <Icon name="check" size="md" />
              <span className="ml-1">Complete</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default StepByStepWizard;
